import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Hash, Loader2, Share2, User as UserIcon } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { commsApi } from '@/utils/commsApi';
import { buildShareText } from '@/utils/markdownText';

// Post an assistant answer into a team channel or a 1:1 conversation as a normal text message from the user.
export const ShareAnswerMenu = ({ message, modelName, question, teams = [], contacts = [], user }) => {
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);

  const targets = [
    ...teams.map((t) => ({ kind: 'team', id: t.id, name: t.name })),
    ...contacts.map((c) => ({ kind: 'user', id: c.id, name: c.name })),
  ];

  const share = async (target) => {
    setSharing(true);
    const label = target.kind === 'team' ? `#${target.name}` : target.name;
    try {
      const text = buildShareText({ modelName, userName: user?.name, question, answer: message.content });
      const payload = { message: text, message_type: 'text', client_id: `share-${message.id}-${Date.now()}` };
      if (target.kind === 'team') await commsApi.sendChannel(target.id, payload);
      else await commsApi.send({ ...payload, to_user_id: target.id });
      toast.success(target.kind === 'team' ? `Shared to ${label}` : `Shared with ${label}`, {
        action: { label: 'Open', onClick: () => navigate(`/dashboard/communications?c=${target.kind}:${target.id}`) },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not share to ${label}`);
    } finally {
      setSharing(false);
    }
  };

  const icon = sharing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />;
  if (!targets.length) return null;

  if (targets.length === 1) {
    const only = targets[0];
    const label = only.kind === 'team' ? `Share to #${only.name}` : `Share with ${only.name}`;
    return (
      <button type="button" className="chat-action" onClick={() => share(only)} disabled={sharing} aria-label={label} title={label} data-testid={`chat-share-${message.id}`}>
        {icon}
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="chat-action" disabled={sharing} aria-label="Share this answer" title="Share to a channel or a person" data-testid={`chat-share-${message.id}`}>
          {icon}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="chat-share-menu" data-testid={`chat-share-menu-${message.id}`}>
        {teams.length > 0 && (
          <>
            <DropdownMenuLabel>Channels</DropdownMenuLabel>
            {teams.map((t) => (
              <DropdownMenuItem key={t.id} onSelect={() => share({ kind: 'team', id: t.id, name: t.name })} data-testid={`chat-share-team-${t.id}`}>
                <Hash size={14} /> {t.name}
              </DropdownMenuItem>
            ))}
          </>
        )}
        {contacts.length > 0 && (
          <>
            {teams.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>People</DropdownMenuLabel>
            {contacts.map((c) => (
              <DropdownMenuItem key={c.id} onSelect={() => share({ kind: 'user', id: c.id, name: c.name })} data-testid={`chat-share-user-${c.id}`}>
                <UserIcon size={14} /> {c.name}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
